import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	MenuItem,
	Stack,
	TextField,
} from "@mui/material";
import { useEffect, useState } from "react";

import {
	fetchJobylonStages,
	JobylonStage,
	pushPersonToJobylonJob,
} from "../../api/assignments/jobylon-jobs";

interface JobylonPushDialogProps {
	/** ODI's row id for the Jobylon job behind this role. */
	jobylonJobId: number;
	personId: number;
	roleName: string;
	open: boolean;
	onClose: () => void;
	/** Called once the candidate is on the Jobylon board. */
	onPushed: () => void;
}

/** The stage value meaning "let Jobylon decide", which is what an empty list leaves. */
const JOB_DEFAULT = "";

/**
 * Put a sourced candidate on the customer's own Jobylon board.
 *
 * The stage list is read from the customer's Jobylon feed when the dialog
 * opens, so it is the pipeline as it stands rather than a copy that may have
 * drifted. Leaving the stage unchosen lets Jobylon place the candidate where
 * that job starts its applicants.
 */
export const JobylonPushDialog = ({
	jobylonJobId,
	personId,
	roleName,
	open,
	onClose,
	onPushed,
}: JobylonPushDialogProps) => {
	const [stages, setStages] = useState<JobylonStage[] | null>(null);
	const [chosen, setChosen] = useState<string>(JOB_DEFAULT);
	const [isPushing, setIsPushing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		const controller = new AbortController();
		setError(null);
		setChosen(JOB_DEFAULT);
		fetchJobylonStages(jobylonJobId, controller.signal)
			.then(setStages)
			.catch((err: unknown) => {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(err instanceof Error ? err.message : "Could not load stages");
			});
		return () => controller.abort();
	}, [open, jobylonJobId]);

	const handlePush = async () => {
		setIsPushing(true);
		setError(null);
		try {
			await pushPersonToJobylonJob(
				jobylonJobId,
				personId,
				chosen === JOB_DEFAULT ? undefined : Number(chosen)
			);
			onPushed();
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Could not push to Jobylon");
		} finally {
			setIsPushing(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			data-component="jobylon-push-dialog"
		>
			<DialogTitle>Add to Jobylon</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<DialogContentText>
						{roleName} is run in Jobylon, so this person goes on the
						customer&apos;s own board as a sourced candidate. Jobylon does
						not email them.
					</DialogContentText>
					{stages === null ? (
						<CircularProgress
							size={20}
							data-component="jobylon-stages-loading"
						/>
					) : (
						<TextField
							select
							size="small"
							label="Stage"
							value={chosen}
							onChange={(event) => setChosen(event.target.value)}
							data-component="jobylon-stage-select"
							helperText={
								stages.length === 0
									? "This job has no pipeline stages in Jobylon."
									: "This job's pipeline, as Jobylon has it now."
							}
						>
							<MenuItem value={JOB_DEFAULT}>Let Jobylon decide</MenuItem>
							{stages.map((stage) => (
								<MenuItem
									key={stage.status_id}
									value={String(stage.status_id)}
								>
									{stage.name}
								</MenuItem>
							))}
						</TextField>
					)}
					{error && (
						<Alert severity="error" data-component="jobylon-push-error">
							{error}
						</Alert>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} data-component="jobylon-push-cancel">
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handlePush}
					disabled={isPushing || stages === null}
					startIcon={isPushing ? <CircularProgress size={14} /> : undefined}
					data-component="jobylon-push-confirm"
				>
					Add to Jobylon
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default JobylonPushDialog;
